import type { ReactNode } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { KeyRound, LogOut, RotateCw, ShieldBan, UserX } from 'lucide-react';
import { isPermissionKey, type PermissionKey } from '@nexa/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { forceLogout } from '@/auth/force-logout';
import { useSessionIdentity } from '@/auth/session-claims';
import { permissionGroupLabel, permissionGroupOf, permissionLabel } from '@/lib/access/labels';

// P8 "Acceso denegado" (users-roles-permissions §5) — three faces of one question:
//
//   - <PermissionDenied>  — signed in, missing ONE permission for this screen. Rendered in
//                           place by the route guards (the URL stays, so the screen opens
//                           by itself the moment an admin grants it — `access.changed`).
//   - <NoAccessAssigned>  — signed in, NO role at all (§4.19 default-deny).
//   - <ForbiddenPage>     — the standalone `/forbidden` route, reachable without a session
//                           (wrong tenant / legacy wrong role / `?permission=<key>`).
//
// "Who can grant it": anyone holding "Asignar / quitar roles" (`users.roles.assign`). The
// member list is itself permission-gated (`users.view`), which the person reading this
// typically lacks — so the copy names the role, not people.

const TENANT = import.meta.env.VITE_TENANT ?? '';

/** Centered card frame inside the console's content area — one focused message (§18.5). */
function StateFrame({ children, testId }: { children: ReactNode; testId: string }) {
  return (
    <div
      className="flex min-h-full w-full items-center justify-center p-4 sm:p-6"
      data-testid={testId}
    >
      <Card className="w-full max-w-lg">{children}</Card>
    </div>
  );
}

/** The missing permission: label primary, group + key secondary (§18.3). */
function PermissionDetail({ permission }: { permission: PermissionKey }) {
  const { t } = useTranslation('admin');
  const group = permissionGroupOf(permission);
  return (
    <div className="rounded-md border border-border bg-muted/40 p-3">
      <p className="flex items-start gap-2 text-sm font-medium text-foreground">
        <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <span data-testid="denied-permission-label">{permissionLabel(t, permission)}</span>
      </p>
      <p className="mt-1 break-all pl-6 text-xs text-muted-foreground">
        {group ? `${permissionGroupLabel(t, group)} · ` : null}
        <span className="font-mono">{permission}</span>
      </p>
    </div>
  );
}

function WhoCanGrant() {
  const { t } = useTranslation('admin');
  return (
    <p className="text-sm text-muted-foreground">
      {t('access.denied.whoCanGrant', {
        permission: permissionLabel(t, 'users.roles.assign'),
      })}
    </p>
  );
}

/** In-shell "Acceso denegado" for a screen whose view permission the operator lacks. */
export function PermissionDenied({ permission }: { permission: PermissionKey }) {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const location = useLocation();
  // A deep link opened in a fresh tab has no history to go back to — go home instead.
  const canGoBack = location.key !== 'default';

  return (
    <StateFrame testId="permission-denied">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <ShieldBan className="h-5 w-5 shrink-0 text-destructive" aria-hidden />
          {t('access.denied.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">{t('access.denied.needs')}</p>
        <PermissionDetail permission={permission} />
        <WhoCanGrant />
        <div className="flex flex-col gap-2 sm:flex-row">
          {canGoBack ? (
            <Button variant="outline" className="w-full sm:w-auto" onClick={() => navigate(-1)}>
              {t('access.denied.back')}
            </Button>
          ) : null}
          <Button className="w-full sm:w-auto" onClick={() => navigate('/')}>
            {t('access.denied.home')}
          </Button>
        </div>
      </CardContent>
    </StateFrame>
  );
}

/**
 * "Sin acceso asignado" (§4.19): signed in, but no role in this airline. Nothing in the
 * console is reachable, so the screen explains, names who can help, offers a re-check
 * and a way out. It also clears by itself: an assignment pushes `access.changed`.
 */
export function NoAccessAssigned({
  onRetry,
  retrying = false,
}: {
  onRetry: () => void;
  retrying?: boolean;
}) {
  const { t } = useTranslation('admin');
  const identity = useSessionIdentity();

  return (
    <StateFrame testId="no-access-assigned">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <UserX className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
          {t('access.noAccess.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {identity
            ? t('access.noAccess.body', { name: identity.displayName })
            : t('access.noAccess.bodyAnonymous')}
        </p>
        <p className="text-sm text-muted-foreground">{t('access.noAccess.help')}</p>
        {identity?.operatorUrn ? (
          <dl className="flex flex-col gap-0.5 border-t pt-3 text-xs text-muted-foreground sm:flex-row sm:gap-2">
            <dt className="shrink-0">{t('access.noAccess.account')}</dt>
            <dd className="break-all font-mono">{identity.operatorUrn}</dd>
          </dl>
        ) : null}
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            onClick={onRetry}
            disabled={retrying}
            aria-busy={retrying}
          >
            <RotateCw className={retrying ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} aria-hidden />
            {t('access.noAccess.retry')}
          </Button>
          <Button
            className="w-full sm:w-auto"
            onClick={() => void forceLogout({ reason: 'user-initiated' })}
          >
            <LogOut className="h-4 w-4" aria-hidden />
            {t('access.noAccess.signOut')}
          </Button>
        </div>
      </CardContent>
    </StateFrame>
  );
}

/** The standalone `/forbidden` route — outside the shell, no session required. */
export function ForbiddenPage() {
  const { t } = useTranslation('admin');
  const [params] = useSearchParams();
  const reason = params.get('reason') ?? 'forbidden';
  const rawPermission = params.get('permission');
  const permission = isPermissionKey(rawPermission) ? rawPermission : null;

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background p-4 sm:p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('forbidden.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {permission ? (
            <>
              <p className="text-sm text-muted-foreground">{t('access.denied.needs')}</p>
              <PermissionDetail permission={permission} />
              <WhoCanGrant />
            </>
          ) : (
            <p className="text-sm text-muted-foreground">{t(messageKeyForReason(reason))}</p>
          )}
          {TENANT ? (
            <p className="text-xs text-muted-foreground">
              {t('forbidden.tenantLabel')}{' '}
              <span className="font-mono uppercase">{TENANT}</span>
            </p>
          ) : null}
          <div className="flex flex-col gap-2 sm:flex-row">
            {permission ? (
              <Button className="w-full sm:w-auto" onClick={() => (window.location.href = '/')}>
                {t('access.denied.home')}
              </Button>
            ) : null}
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => (window.location.href = '/login?reason=wrong-tenant')}
            >
              {t('forbidden.switchAccount')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/** Holds KEYS, never language — the page renders them through `t()`. */
function messageKeyForReason(reason: string): string {
  switch (reason) {
    case 'wrong-tenant':
      return 'forbidden.reason.wrongTenant';
    case 'wrong-role':
      return 'forbidden.reason.wrongRole';
    default:
      return 'forbidden.reason.default';
  }
}
